import React, { useState } from 'react';
import styled from 'styled-components';
import { FaCopy, FaCheck, FaTimes } from 'react-icons/fa';

interface CustomerInfoPopupProps {
  isVisible: boolean;
  initialCode?: string;
  initialName?: string;
  onSave: (code: string, name: string) => void;
  onClose: () => void;
}

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const PopupContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 450px;
  padding: 2rem;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.surface};
    color: ${props => props.theme.primary};
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  text-align: center;
  font-weight: 700;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  padding: 1rem;
  background: ${props => props.theme.surface};
  border-radius: 8px;
  border: 2px solid ${props => props.theme.border};
`;

const CodeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CodeValue = styled.div`
  flex: 1;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  padding: 1rem;
  background: ${props => props.theme.surface};
  border-radius: 8px;
  border: 2px solid ${props => props.theme.border};
  font-family: monospace;
`;

const CopyButton = styled.button<{ copied?: boolean }>`
  background: ${props => props.copied ? props.theme.success : props.theme.primary};
  color: white;
  border: none;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  min-width: 100px;
  justify-content: center;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CustomerInfoPopup: React.FC<CustomerInfoPopupProps> = ({
  isVisible,
  initialCode = '',
  initialName = '',
  onSave,
  onClose
}) => {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopyCode = () => {
    if (initialCode.trim()) {
      navigator.clipboard.writeText(initialCode.trim()).then(() => {
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      }).catch((err) => {
        console.error('Failed to copy:', err);
      });
    }
  };

  const handleClose = () => {
    setCopiedCode(false);
    onClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Overlay isVisible={isVisible} onClick={handleClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          {React.createElement(FaTimes as any, { size: 20 })}
        </CloseButton>

        <Title>Customer Information</Title>

        <InfoSection>
          <InfoItem>
            <InfoLabel>Customer Name</InfoLabel>
            <InfoValue>{initialName || 'N/A'}</InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>Customer Code</InfoLabel>
            <CodeContainer>
              <CodeValue>{initialCode || 'N/A'}</CodeValue>
              {initialCode && (
                <CopyButton
                  copied={copiedCode}
                  onClick={handleCopyCode}
                >
                  {copiedCode ? (
                    <>
                      {React.createElement(FaCheck as any, { size: 14 })}
                      Copied
                    </>
                  ) : (
                    <>
                      {React.createElement(FaCopy as any, { size: 14 })}
                      Copy
                    </>
                  )}
                </CopyButton>
              )}
            </CodeContainer>
          </InfoItem>
        </InfoSection>
      </PopupContainer>
    </Overlay>
  );
};

export default CustomerInfoPopup;

